import React, { useRef, useEffect, useState } from 'react';
import { Table, Input, Select, Space, Empty, Button, Popconfirm, Tag, Tooltip } from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  DeleteOutlined,
  EyeOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { UserListItem, UserListResponse } from '../../services/admin-users.service';

export interface UserSearchTableProps {
  loading: boolean;
  data?: UserListResponse;
  onSearch: (search: string) => void;
  onRoleChange: (role: string | undefined) => void;
  onPageChange: (page: number, pageSize: number) => void;
  onDelete: (userId: number, email: string) => Promise<void>;
  onViewDetails: (userId: number) => void;
  search?: string;
  selectedRole?: string;
}

const ROLE_OPTIONS = [
  { label: 'Todos os perfis', value: undefined },
  { label: 'Participante', value: 'participant' },
  { label: 'Contratante', value: 'contractor' },
  { label: 'Administrador', value: 'administrator' },
];

const VALIDATION_LEVEL_LABELS: Record<string, string> = {
  LEVEL_1: 'Nível 1',
  LEVEL_2: 'Nível 2',
  LEVEL_3: 'Nível 3',
};

const ROLE_LABELS: Record<string, string> = {
  participant: 'Participante',
  contractor: 'Contratante',
  administrator: 'Administrador',
};

// Maps Cognito UserStatus values to pt-BR labels and tag colors
const COGNITO_STATUS_MAP: Record<string, { label: string; color: string }> = {
  CONFIRMED: { label: 'Confirmado', color: 'green' },
  UNCONFIRMED: { label: 'Não confirmado', color: 'orange' },
  FORCE_CHANGE_PASSWORD: { label: 'Trocar senha', color: 'gold' },
  RESET_REQUIRED: { label: 'Reset necessário', color: 'volcano' },
  COMPROMISED: { label: 'Comprometido', color: 'red' },
  ARCHIVED: { label: 'Arquivado', color: 'default' },
  UNKNOWN: { label: 'Desconhecido', color: 'default' },
};

export const UserSearchTable: React.FC<UserSearchTableProps> = ({
  loading,
  data,
  onSearch,
  onRoleChange,
  onPageChange,
  onDelete,
  onViewDetails,
  search = '',
  selectedRole = undefined,
}) => {
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [localSearch, setLocalSearch] = useState(search);
  const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => onSearch(localSearch), 400);
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [localSearch, onSearch]);

  const handleDelete = async (userId: number, email: string) => {
    setDeletingIds((prev) => new Set(prev).add(userId));
    try {
      await onDelete(userId, email);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  const columns: ColumnsType<UserListItem> = [
    {
      title: 'Nome Preferido',
      dataIndex: 'preferredName',
      key: 'preferredName',
      render: (text: string | null) => text || '-',
      width: 160,
    },
    {
      title: 'E-mail',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => (
        <span title={email} style={{ wordBreak: 'break-word' }}>
          {email}
        </span>
      ),
      width: 220,
    },
    {
      title: 'Perfil',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => ROLE_LABELS[role] || role,
      width: 120,
    },
    {
      title: 'Data de Criação',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY HH:mm'),
      width: 150,
    },
    {
      title: 'Nível de Validação',
      dataIndex: 'validationLevel',
      key: 'validationLevel',
      render: (level: string | null) =>
        level ? VALIDATION_LEVEL_LABELS[level] || level : '-',
      width: 130,
    },
    {
      title: 'Data de Validação',
      dataIndex: 'validatedAt',
      key: 'validatedAt',
      render: (date: string | null) =>
        date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '-',
      width: 150,
    },
    {
      title: 'Status Cognito',
      dataIndex: 'cognitoUserStatus',
      key: 'cognitoUserStatus',
      render: (status: string | null) => {
        if (!status) return <span style={{ color: '#bbb' }}>-</span>;
        const mapped = COGNITO_STATUS_MAP[status] ?? { label: status, color: 'default' };
        return <Tag color={mapped.color}>{mapped.label}</Tag>;
      },
      width: 140,
    },
    {
      title: (
        <Tooltip title="Email verificado no Cognito">
          Email Verificado
        </Tooltip>
      ),
      dataIndex: 'cognitoEmailVerified',
      key: 'cognitoEmailVerified',
      render: (verified: boolean | null) => {
        if (verified === null) return <span style={{ color: '#bbb' }}>-</span>;
        return verified ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />
        );
      },
      width: 130,
      align: 'center' as const,
    },
    {
      title: (
        <Tooltip title="Conta habilitada no Cognito">
          Conta Ativa
        </Tooltip>
      ),
      dataIndex: 'cognitoEnabled',
      key: 'cognitoEnabled',
      render: (enabled: boolean | null) => {
        if (enabled === null) return <span style={{ color: '#bbb' }}>-</span>;
        return enabled ? (
          <Tag color="green">Habilitada</Tag>
        ) : (
          <Tag color="red">Desabilitada</Tag>
        );
      },
      width: 120,
      align: 'center' as const,
    },
    {
      title: 'Ação',
      key: 'action',
      fixed: 'right' as const,
      width: 110,
      align: 'center' as const,
      render: (_: unknown, record: UserListItem) => (
        <Space size="small">
          <Tooltip title="Ver detalhes">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => onViewDetails(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="Excluir usuário"
            description={
              <span>
                Tem certeza que deseja excluir <strong>{record.email}</strong>?
                <br />
                Esta ação é irreversível e removerá o usuário da Pepoll e do Cognito.
              </span>
            }
            icon={<QuestionCircleOutlined style={{ color: '#ff4d4f' }} />}
            okText="Excluir"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record.id, record.email)}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              loading={deletingIds.has(record.id)}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination: TablePaginationConfig) => {
    onPageChange(pagination.current || 1, pagination.pageSize || 20);
  };

  return (
    <div style={{ width: '100%' }}>
      <Space style={{ marginBottom: 16, width: '100%' }} wrap>
        <Input.Search
          placeholder="Buscar por nome ou e-mail"
          prefix={<SearchOutlined />}
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />

        <Select
          style={{ width: 200 }}
          options={ROLE_OPTIONS}
          value={selectedRole}
          onChange={onRoleChange}
          placeholder="Filtrar por perfil"
        />

        {(localSearch || selectedRole) && (
          <Button
            icon={<ClearOutlined />}
            onClick={() => {
              setLocalSearch('');
              onRoleChange(undefined);
            }}
          >
            Limpar Filtros
          </Button>
        )}
      </Space>

      <Table
        columns={columns}
        dataSource={(data?.items ?? []).map((item: any) => ({ ...item, key: item.id }))}
        loading={loading}
        locale={{ emptyText: loading ? ' ' : <Empty description="Nenhum usuário encontrado" /> }}
        pagination={
          data && data.totalItems > 0
            ? {
                current: data.page,
                pageSize: data.pageSize,
                total: data.totalItems,
                showTotal: (total) => `Total: ${total} usuários`,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                position: ['topRight', 'bottomRight'] as any,
              }
            : false
        }
        onChange={handleTableChange}
        scroll={{ x: 1600 }}
        size="small"
      />
    </div>
  );
};

export default UserSearchTable;
